import type { VolunteerOrganizationLead } from "@entities";
import { VolunteerOrganizationLead as VolunteerOrganizationLeadEntity } from "@entities";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import { volunteerOrganizationLeadRepo } from "@repositories";
import { partnerOrgLeadContactChannelLabel } from "@shared/helpers/partner-organization-lead-contact";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { SingleRowAction } from "@shared/frontend/hyper-table/inner-components/hyper-table-expand-column/HyperTableExpandColumn";
import { type ColumnProps } from "@shared/frontend/hyper-table/types";
import { useCallback, useMemo, useState } from "react";
import { remult, type EntityFilter } from "remult";
import { Roles } from "@shared/Roles";
import { useAlert } from "../../../contexts/AlertProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import {
  organizationLeadFilterItems,
  VOLUNTEER_ORGANIZATION_LEAD_FILTERS,
} from "../organization-lead-filters";
import { VolunteerOrganizationLeadDialog } from "./VolunteerOrganizationLeadDialog";

const LEAD_COLUMNS: ColumnProps<VolunteerOrganizationLead>[] = [
  {
    field: "name",
    caption: "שם הארגון",
    enableSorting: true,
    minSize: 220,
  },
  {
    field: "handlingManagerId",
    caption: "מנהל מלווה",
    minSize: 180,
    cell: ({ row }) => row.original.handlingManager?.name ?? "",
  },
  {
    field: "phoneNumber",
    caption: "טלפון",
    minSize: 140,
    cell: ({ row }) => row.original.phoneNumber ?? "",
  },
  {
    field: "notes",
    caption: "הערות",
    minSize: 160,
    cell: ({ row }) => row.original.notes?.slice(0, 80) ?? "",
  },
  {
    field: "contactChannel",
    caption: "דרך יצירת קשר",
    minSize: 140,
    cell: ({ row }) =>
      partnerOrgLeadContactChannelLabel(row.original.contactChannel),
  },
  {
    field: "createdAt",
    caption: "תאריך יצירה",
    enableSorting: true,
    minSize: 140,
    cell: ({ row }) =>
      row.original.createdAt
        ? new Date(row.original.createdAt).toLocaleDateString("he-IL")
        : "",
  },
];

type Props = {
  onConvertLead: (lead: VolunteerOrganizationLead) => void;
};

export function VolunteerOrganizationLeadsTable({ onConvertLead }: Props) {
  const { HyperTable } = useCreateHyperTable(VolunteerOrganizationLeadEntity);
  const canManage = remult.isAllowed(Roles.AdminsAndManager);
  const { setAlertText, setSeverity, setTime } = useAlert();
  const question = useQuestion();
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<VolunteerOrganizationLead>();

  const openAdd = useCallback(() => {
    setSelectedLead(undefined);
    setLeadDialogOpen(true);
  }, []);

  const openEdit = useCallback((lead: VolunteerOrganizationLead) => {
    setSelectedLead(lead);
    setLeadDialogOpen(true);
  }, []);

  const closeLeadDialog = useCallback(() => {
    setLeadDialogOpen(false);
    setSelectedLead(undefined);
  }, []);

  const deleteLeads = useCallback(
    async (
      where: EntityFilter<VolunteerOrganizationLead>,
      successMessage = "הלידים נמחקו בהצלחה",
    ) => {
      try {
        await volunteerOrganizationLeadRepo.deleteMany({ where });
        setAlertText(successMessage);
        setSeverity("success");
        setTime(2000);
        return true;
      } catch {
        setAlertText("מחיקה נכשלה");
        setSeverity("error");
        setTime(3000);
        return false;
      }
    },
    [setAlertText, setSeverity, setTime],
  );

  const getMenuActions = useCallback<SingleRowActionGetter<VolunteerOrganizationLead>>(
    () => {
      const actions: SingleRowAction<VolunteerOrganizationLead>[] = [];
      if (canManage) {
        actions.push(
          {
            text: "הפוך לארגון מתנדבים",
            icon: <ApartmentOutlinedIcon />,
            onClick: (_: unknown, row: { original: VolunteerOrganizationLead }) => {
              onConvertLead(row.original);
              return { refetch: false };
            },
          },
          {
            text: "עריכה",
            icon: <EditIcon />,
            onClick: (_: unknown, row: { original: VolunteerOrganizationLead }) => {
              openEdit(row.original);
              return { refetch: false };
            },
          },
          {
            text: "מחיקה",
            icon: <DeleteOutlineIcon />,
            onClick: async (
              _: unknown,
              row: { original: VolunteerOrganizationLead },
            ) => {
              const yes = await question(
                `האם למחוק את הליד "${row.original.name}"?`,
              );
              if (!yes) return { refetch: false };
              const deleted = await deleteLeads(
                { id: row.original.id },
                "הליד נמחק בהצלחה",
              );
              return { refetch: deleted };
            },
          },
        );
      }
      return actions;
    },
    [canManage, deleteLeads, onConvertLead, openEdit, question],
  );

  const buttons = useMemo<ActionButton<VolunteerOrganizationLead>[]>(
    () => [
      {
        icon: <DeleteOutlineIcon />,
        title: "מחיקה",
        onClick: async ({ whereToUpdate }) => {
          const yes = await question("האם למחוק את הלידים שנבחרו?");
          if (!yes) return { refetch: false };
          const deleted = await deleteLeads(whereToUpdate);
          return { refetch: deleted, resetSelection: deleted };
        },
      },
    ],
    [deleteLeads, question],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 0",
        flexDirection: "column",
        minHeight: 0,
        width: "100%",
        height: "100%",
      }}
    >
      <HyperTable
        tableId="volunteer-organization-leads-table"
        title={<Box sx={{ flex: 1 }} />}
        components={{
          ...(canManage
            ? {
                addRowButton: {
                  onClick: openAdd,
                  content: "ליד חדש",
                },
              }
            : {}),
        }}
        findConfig={{
          initialOrderBy: { createdAt: "desc" },
          where: { convertedAt: null } as unknown as EntityFilter<VolunteerOrganizationLead>,
          include: { handlingManager: true },
        }}
        onRowClick={canManage ? (_, row) => openEdit(row.original) : undefined}
      >
        <HyperTable.Toolbar disableViewOptions>
          {organizationLeadFilterItems(VOLUNTEER_ORGANIZATION_LEAD_FILTERS)}
        </HyperTable.Toolbar>
        {canManage && <HyperTable.SelectColumn />}
        <HyperTable.MenuColumn actions={getMenuActions} />
        {LEAD_COLUMNS.map((column) => (
          <HyperTable.Column key={column.field} {...column} />
        ))}
        {canManage && <HyperTable.ActionsBar buttons={buttons} />}
      </HyperTable>

      {canManage && leadDialogOpen && (
        <VolunteerOrganizationLeadDialog
          open={leadDialogOpen}
          lead={selectedLead ?? null}
          onClose={closeLeadDialog}
          onSaved={async () => {
            await HyperTable.context.current?.refetch("invalidate");
            setAlertText(
              selectedLead ? "הליד עודכן בהצלחה" : "הליד נוצר בהצלחה",
            );
            setSeverity("success");
            setTime(2000);
          }}
        />
      )}
    </Box>
  );
}
